import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Space,
  Typography,
  message,
  Empty,
  Form,
  Input,
  Select,
  Col,
  Modal,
} from "antd";
import {
  PlusOutlined,
  ExportOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import FilterPanel from "../../components/common/FilterPanel";
import HQDisplayGroupTable from "./components/HQDisplayGroupTable";
import BatchActionToolbar from "../../components/common/BatchActionToolbar";
import {
  HQDisplayGroup,
  HQDisplayGroupFilters,
  PaginationParams,
  TableColumnKey,
  SortField,
  SortOrder,
} from "../../types/hqDisplayGroup";
import hqDisplayGroupService from "../../services/hqDisplayGroupService";
import categoryService from "../../services/categoryService";
// 修改引用为新的动态状态选项生成函数
import { getStatusOptions } from "../../utils/hqDisplayGroupConstants";

// 扩展PaginationParams接口，添加total属性用于UI分页
interface ExtendedPaginationParams extends PaginationParams {
  total: number;
}

const { Title } = Typography;

const HQDisplayGroupList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // State for display groups data
  const [displayGroups, setDisplayGroups] = useState<HQDisplayGroup[]>([]);

  // State for loading indicators
  const [loading, setLoading] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  // State for filters and pagination
  const [filters, setFilters] = useState<HQDisplayGroupFilters>({});
  const [pagination, setPagination] = useState<ExtendedPaginationParams>({
    page: 1,
    pageSize: 10,
    total: 0,
    sortBy: "createTime" as SortField,
    sortOrder: "desc" as SortOrder,
  });

  // State for selected rows
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // State for category options
  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  
  // State for division options
  const [divisionOptions, setDivisionOptions] = useState<{ label: string; value: string }[]>([]);

  // Fetch categories for dropdown
  const fetchCategories = async () => {
    try {
      const response = await categoryService.listCategories();
      if (response.success && response.data) {
        // Extract unique categories from the data
        const uniqueCategories = Array.from(
          new Map(
            response.data.map(cat => [
              cat.subClassCode,
              {
                label: `${cat.subClassName}`,
                value: cat.subClassCode,
              }
            ])
          ).values()
        );
        setCategoryOptions(uniqueCategories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error(t("hqDisplayGroup.loadCategoriesFailed"));
    }
  };
  
  // Fetch divisions for dropdown
  const fetchDivisions = async () => {
    try {
      const response = await categoryService.listDivision();
      if (response.success && response.data) {
        setDivisionOptions(response.data);
      }
    } catch (error) {
      console.error("Error fetching divisions:", error);
      message.error(t("hqDisplayGroup.loadDivisionsFailed"));
    }
  };

  // Fetch display groups data
  const fetchDisplayGroups = async (refresh = false) => {
    setLoading(true);
    try {
      // If refresh is true, reset data first
      if (refresh) {
        await hqDisplayGroupService.getHQDisplayGroups();
      }

      const response = await hqDisplayGroupService.getHQDisplayGroups(
        filters,
        pagination
      );
      if (response.success && response.data) {
        setDisplayGroups(response.data.tableData || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data?.paging?.totalCount || 0,
        }));
      } else {
        message.error(response.errorMessage || t("hqDisplayGroup.loadFailed"));
        setDisplayGroups([]);
      }
    } catch (error) {
      console.error("Error fetching display groups:", error);
      message.error(t("hqDisplayGroup.loadFailed"));
      setDisplayGroups([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchDisplayGroups();
    fetchCategories();
    fetchDivisions();
  }, []);

  // Fetch data when filters or pagination change
  useEffect(() => {
    fetchDisplayGroups();
  }, [
    filters,
    pagination.page,
    pagination.pageSize,
    pagination.sortBy,
    pagination.sortOrder,
  ]);

  // Handle filter change
  const handleFilterChange = (newFilters: HQDisplayGroupFilters) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    // 不再直接调用fetchDisplayGroups，让useEffect来处理
  };

  // Handle filter reset
  const handleFilterReset = () => {
    form.resetFields();
    setFilters({});
    setPagination((prev) => ({
      ...prev,
      page: 1,
      sortBy: "createTime" as SortField,
      sortOrder: "desc",
    }));
    // 不再直接调用fetchDisplayGroups，让useEffect来处理
  };

  // Handle page change
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      pageSize,
    }));
    // 不再直接调用fetchDisplayGroups，让useEffect来处理
  };

  // Handle sort change
  const handleSortChange = (
    sortBy: TableColumnKey,
    sortOrder: "asc" | "desc"
  ) => {
    // 将TableColumnKey转换为SortField，排除'status'和'actions'等不支持的字段
    const validSortField = (field: TableColumnKey): SortField | undefined => {
      const validFields: Record<string, SortField> = {
        code: "code",
        name: "name",
        mainDivision: "mainDivision",
        createTime: "createTime",
        updateTime: "updateTime",
        lastSyncTime: "lastSyncTime",
        associatedStoreGroups: "associatedStoreGroups",
      };
      return validFields[field];
    };

    setPagination((prev) => ({
      ...prev,
      sortBy: validSortField(sortBy),
      sortOrder,
    }));
  };

  // Handle view display group
  const handleViewDisplayGroup = (code: string) => {
    navigate(`/hq-display-groups/${code}`);
  };

  // Handle edit display group
  const handleEditDisplayGroup = (code: string) => {
    navigate(`/hq-display-groups/${code}/edit`);
  };

  // Handle create new display group
  const handleCreateDisplayGroup = () => {
    navigate("/hq-display-groups/create");
  };

  // Clear selected rows
  const handleClearSelection = () => {
    setSelectedRowKeys([]);
  };

  // Handle batch activation
  const handleBatchActivate = async () => {
    if (selectedRowKeys.length === 0) return;

    Modal.confirm({
      title: t("hqDisplayGroup.batchActivate"),
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: t("hqDisplayGroup.batchActivateConfirm", {
        count: selectedRowKeys.length,
      }),
      onOk: async () => {
        setLoading(true);
        try {
          const numericIds = selectedRowKeys.map((key) => parseInt(key, 10));
          const response = await hqDisplayGroupService.batchUpdateStatus(
            numericIds,
            "ENABLE"
          );
          if (response.success && response.data) {
            message.success(
              t("hqDisplayGroup.batchActivateSuccess", {
                success: selectedRowKeys.length,
              })
            );
            fetchDisplayGroups();
            handleClearSelection();
          } else {
            message.error(
              response.errorMessage || t("hqDisplayGroup.batchActivateFailed")
            );
          }
        } catch (error) {
          console.error("Batch activate error:", error);
          message.error(t("hqDisplayGroup.batchActivateFailed"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Handle batch deactivation
  const handleBatchDeactivate = async () => {
    if (selectedRowKeys.length === 0) return;

    Modal.confirm({
      title: t("hqDisplayGroup.batchDeactivate"),
      icon: <StopOutlined style={{ color: "#faad14" }} />,
      content: t("hqDisplayGroup.batchDeactivateConfirm", {
        count: selectedRowKeys.length,
      }),
      onOk: async () => {
        setLoading(true);
        try {
          const numericIds = selectedRowKeys.map((key) => parseInt(key, 10));
          const response = await hqDisplayGroupService.batchUpdateStatus(
            numericIds,
            "DISABLE"
          );
          if (response.success && response.data) {
            message.success(
              t("hqDisplayGroup.batchDeactivateSuccess", {
                success: selectedRowKeys.length,
              })
            );
            fetchDisplayGroups();
            handleClearSelection();
          } else {
            message.error(
              response.errorMessage || t("hqDisplayGroup.batchDeactivateFailed")
            );
          }
        } catch (error) {
          console.error("Batch deactivate error:", error);
          message.error(t("hqDisplayGroup.batchDeactivateFailed"));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <div>
      <FilterPanel
        form={form}
        onFilter={(values) => {
          // 创建过滤器对象
          const newFilters: HQDisplayGroupFilters = {
            code: values.code,
            name: values.name,
            search: values.search,
            status: values.status,
            category: values.category,
            mainDivision: values.mainDivision,
            hasWhitelistItems: values.hasWhitelistItems,
            hasBlacklistItems: values.hasBlacklistItems,
          };
          
          // 移除空值字段
          Object.keys(newFilters).forEach(key => {
            const value = newFilters[key];
            if (value === undefined || value === null || value === '') {
              delete newFilters[key];
            }
          });
          
          // 如果 hasWhitelistItems 选择了 'all'，则不传递给后端
          if (newFilters.hasWhitelistItems === 'all') {
            delete newFilters.hasWhitelistItems;
          }
          
          // 如果 hasBlacklistItems 选择了 'all'，则不传递给后端
          if (newFilters.hasBlacklistItems === 'all') {
            delete newFilters.hasBlacklistItems;
          }
          
          handleFilterChange(newFilters);
        }}
        onReset={handleFilterReset}
        className="filter-card"
        title={t("menu.hqDisplayGroup")}
      >
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="code" label={t("hqDisplayGroup.code")}>
            <Input
              placeholder={t("hqDisplayGroup.form.codePlaceholder")}
              disabled={loading}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="name" label={t("hqDisplayGroup.name")}>
            <Input
              placeholder={t("hqDisplayGroup.form.namePlaceholder")}
              disabled={loading}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="status" label={t("hqDisplayGroup.filter.status")}>
            <Select
              placeholder={t("hqDisplayGroup.filter.status")}
              options={getStatusOptions(t)}
              disabled={loading}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            name="mainDivision"
            label={t("hqDisplayGroup.filter.mainDivision")}
          >
            <Select
              placeholder={t("hqDisplayGroup.filter.mainDivision")}
              options={divisionOptions}
              disabled={loading}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            name="category"
            label={t("hqDisplayGroup.filter.category")}
          >
            <Select
              placeholder={t("hqDisplayGroup.filter.category")}
              options={categoryOptions}
              disabled={loading}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            name="hasWhitelistItems"
            label={t("hqDisplayGroup.filter.hasWhitelistItems")}
          >
            <Select
              placeholder={t("hqDisplayGroup.filter.hasWhitelistItems")}
              options={[
                { label: t('common.all'), value: 'all' },
                { label: t('common.yes'), value: 'YES' },
                { label: t('common.no'), value: 'NO' },
              ]}
              disabled={loading}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            name="hasBlacklistItems"
            label={t("hqDisplayGroup.filter.hasBlacklistItems")}
          >
            <Select
              placeholder={t("hqDisplayGroup.filter.hasBlacklistItems")}
              options={[
                { label: t('common.all'), value: 'all' },
                { label: t('common.yes'), value: 'YES' },
                { label: t('common.no'), value: 'NO' },
              ]}
              disabled={loading}
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card className="table-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <BatchActionToolbar
            selectedCount={selectedRowKeys.length}
            actions={[
              {
                key: "activate",
                label: t("hqDisplayGroup.batchActivate"),
                icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
                onClick: handleBatchActivate,
                disabled: loading || selectedRowKeys.length === 0,
              },
              {
                key: "deactivate",
                label: t("hqDisplayGroup.batchDeactivate"),
                icon: <StopOutlined style={{ color: "#faad14" }} />,
                onClick: handleBatchDeactivate,
                disabled: loading || selectedRowKeys.length === 0,
              },
            ]}
            onClearSelection={handleClearSelection}
          />
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateDisplayGroup}
            >
              {t("hqDisplayGroup.new")}
            </Button>
            {/* <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
              loading={exportLoading}
              disabled={loading}
            >
              导出
            </Button> */}
          </Space>
        </div>

        {displayGroups.length > 0 ? (
          <HQDisplayGroupTable
            data={displayGroups}
            pagination={pagination}
            loading={loading}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            onRefresh={() => fetchDisplayGroups()}
            onView={handleViewDisplayGroup}
            onEdit={handleEditDisplayGroup}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                {Object.keys(filters).length > 0
                  ? t("hqDisplayGroup.noMatchingData")
                  : t("hqDisplayGroup.noDataFound")}
              </span>
            }
            style={{ margin: "40px 0" }}
          />
        )}
      </Card>
    </div>
  );
};

export default HQDisplayGroupList;
